import { ChangeDetectorRef, OnDestroy, Input, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYTService } from '../../../../../../fikoyt.service';
import { FikoSecililer } from '../FikoSecililer';

export interface ToolConfiguration {
    toolType: 'ball' | 'flat' | 'bullnose' | 'chamfer';
    radius: number;
    cornerRadius?: number;
    angle?: number;
}

export interface RadiusAnalysisSettings {
    toolConfig: ToolConfiguration;
    minRadius: number;
    maxRadius: number;
    showHeatmap: boolean;
}

@Component({
    selector: 'fikoyt-radius-analysis',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './radius-analysis.component.html',
    styleUrls: ['./radius-analysis.component.scss']
})
export class RadiusAnalysisComponent implements OnInit, OnDestroy {
    @Input() anaGosterim: any = null;
    @Input() pencere: any = null;

    subsList: Subscription[] = [];
    haritaOlaylar: Map<string, any> = new Map();

    // Radius analizi özellikleri
    shaderMaterial: THREE.ShaderMaterial | null = null;
    aktifMeshler: THREE.Mesh[] = [];
    orijinalMaterials: Map<THREE.Mesh, THREE.Material | THREE.Material[]> = new Map();
    moldDirection: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
    analizAktif: boolean = false;

    // Yüzey seçimi (undercut tarzı)
    yonSecimAktif: boolean = false;
    fikoSecimYuzey: any = null;
    secilenFaceNormal: THREE.Vector3 | null = null;

    // Radius analizi ayarları
    public settings: RadiusAnalysisSettings = {
        toolConfig: {
            toolType: 'ball',
            radius: 5.0
        },
        minRadius: 0.1,
        maxRadius: 50.0,
        showHeatmap: true
    };

    // Shader Lighting Parametreleri (undercut tarzı)
    keyLightIntensity: number = 0.6;
    fillLightIntensity: number = 0.4;
    rimLightIntensity: number = 0.3;
    ambientLight: number = 0.3;

    // AO & Edge
    aoIntensity: number = 3.0;
    aoMax: number = 0.5;
    edgeIntensity: number = 2.0;
    edgeDarkness: number = 0.3;

    // Curvature scale (kalibrasyon)
    public curvatureScale: number = 1.0;

    // Radius renk aralıkları
    radiusRanges: {
        min: number;
        max: number;
        renk: string;
        isim: string;
    }[] = [
        { min: 0, max: 30, renk: '#9C27B0', isim: 'Otuzluk Takım' },
    ];

    // Yeni aralık formu
    yeniAralik = { min: 0, max: 10, renk: '#ffffff', isim: 'Yeni Aralık' };

    // Pre-allocated buffers for zero-allocation operations
    private readonly _tmpVec3A: THREE.Vector3 = new THREE.Vector3();
    private readonly _tmpVec3B: THREE.Vector3 = new THREE.Vector3();
    private readonly _tmpVec3C: THREE.Vector3 = new THREE.Vector3();
    private readonly _tmpMat3: THREE.Matrix3 = new THREE.Matrix3();
    private readonly _tmpColor: THREE.Color = new THREE.Color();

    constructor(
        public readonly FikoYT: FikoYTService,
        public readonly CD: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.shaderOlustur();
    }

    public baslat(anaGosterim?: any) {
        this.anaGosterim = anaGosterim;

        if (!this.shaderMaterial) {
            this.shaderOlustur();
        }

        // FikoOlcumler mesh'lerini raycast'ten kapat
        if (this.anaGosterim?.sahneler?.[0]) {
            this.anaGosterim.sahneler[0].traverse((child: any) => {
                if (child instanceof THREE.Mesh &&
                    (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
                    child.userData.raycastable = false;
                }
            });
        }

        this.CD.detectChanges();
    }

    public bitir() {
        this.analiziDurdur();
        this.yuzeySecimiDurdur();

        // FikoOlcumler'leri geri raycast'e aç
        if (this.anaGosterim?.sahneler?.[0]) {
            this.anaGosterim.sahneler[0].traverse((child: any) => {
                if (child instanceof THREE.Mesh &&
                    (child.userData?.type === 'FikoOlcumler' || child.parent?.name === 'Ölçümler')) {
                    child.userData.raycastable = true;
                }
            });
        }
    }

    private shaderOlustur() {
        const sortedRanges = [...this.radiusRanges].sort((a, b) => a.min - b.min);
        const maxRanges = 10;
        const ranges = new Float32Array(maxRanges * 2);
        const colors = new Float32Array(maxRanges * 3);

        // Use pre-allocated color buffer to avoid allocations in loop
        for (let i = 0; i < maxRanges; i++) {
            if (i < sortedRanges.length) {
                const range = sortedRanges[i];
                ranges[i * 2] = range.min;
                ranges[i * 2 + 1] = range.max;

                // Reuse _tmpColor buffer instead of creating new Color
                this._tmpColor.set(range.renk);
                colors[i * 3] = this._tmpColor.r;
                colors[i * 3 + 1] = this._tmpColor.g;
                colors[i * 3 + 2] = this._tmpColor.b;
            } else {
                ranges[i * 2] = 999.0;
                ranges[i * 2 + 1] = 999.0;
                colors[i * 3] = 0.0;
                colors[i * 3 + 1] = 0.0;
                colors[i * 3 + 2] = 0.0;
            }
        }

        this.shaderMaterial = new THREE.ShaderMaterial({
            uniforms: {
                toolRadius: { value: this.settings.toolConfig.radius },
                moldDirection: { value: this.moldDirection.clone() },
                radiusRanges: { value: ranges },
                renkler: { value: colors },
                aralikSayisi: { value: Math.min(sortedRanges.length, maxRanges) },
                curvatureScale: { value: this.curvatureScale },
                // Lighting
                keyLightPos: { value: new THREE.Vector3(0.7, 1.0, 0.5) },
                keyLightIntensity: { value: this.keyLightIntensity },
                fillLightPos: { value: new THREE.Vector3(-0.5, 0.3, 0.5) },
                fillLightIntensity: { value: this.fillLightIntensity },
                rimLightPos: { value: new THREE.Vector3(0.0, 0.0, -1.0) },
                rimLightIntensity: { value: this.rimLightIntensity },
                ambientLight: { value: this.ambientLight },
                // AO & Edge
                aoIntensity: { value: this.aoIntensity },
                aoMax: { value: this.aoMax },
                edgeIntensity: { value: this.edgeIntensity },
                edgeDarkness: { value: this.edgeDarkness }
            },
            side: THREE.DoubleSide,
            transparent: true,
            vertexShader: `
                varying vec3 vNormal;
                varying vec3 vPosition;
                varying vec3 vWorldNormal;
                varying vec3 vWorldPosition;
                varying vec3 vViewPosition;

                void main() {
                    vNormal = normalize(normalMatrix * normal);
                    vPosition = position;

                    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
                    vWorldNormal = normalize(mat3(modelMatrix) * normal);
                    vWorldPosition = worldPosition.xyz;

                    vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
                    vViewPosition = viewPosition.xyz;

                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: `
                uniform float toolRadius;
                uniform vec3 moldDirection;
                uniform float radiusRanges[20];
                uniform float renkler[30];
                uniform int aralikSayisi;
                uniform float curvatureScale;

                // Lighting uniforms
                uniform vec3 keyLightPos;
                uniform float keyLightIntensity;
                uniform vec3 fillLightPos;
                uniform float fillLightIntensity;
                uniform vec3 rimLightPos;
                uniform float rimLightIntensity;
                uniform float ambientLight;

                // AO & Edge uniforms
                uniform float aoIntensity;
                uniform float aoMax;
                uniform float edgeIntensity;
                uniform float edgeDarkness;

                varying vec3 vNormal;
                varying vec3 vPosition;
                varying vec3 vWorldNormal;
                varying vec3 vWorldPosition;
                varying vec3 vViewPosition;

                void main() {
                    vec3 worldNormal = normalize(vWorldNormal);
                    vec3 viewNormal = normalize(vNormal);
                    vec3 moldDir = normalize(moldDirection);

                    // Yön kontrolü - sadece iç radiüsleri (mold direction'a ters bakan yüzeyler)
                    float directionFactor = dot(worldNormal, moldDir);

                    // Pozisyon ve normal türevleri (screen-space derivatives)
                    vec3 dPosdx = dFdx(vWorldPosition);
                    vec3 dPosdy = dFdy(vWorldPosition);
                    vec3 dNormdx = dFdx(worldNormal);
                    vec3 dNormdy = dFdy(worldNormal);

                    // Principal curvature hesabı (Catia/profesyonel CAD tarzı)
                    // κ = |dN/ds| where ds = arc length
                    float lenPosx = length(dPosdx);
                    float lenPosy = length(dPosdy);

                    // Sıfıra bölme kontrolü
                    lenPosx = max(lenPosx, 0.0001);
                    lenPosy = max(lenPosy, 0.0001);

                    // Curvature vektörleri
                    float kx = length(dNormdx) / lenPosx;
                    float ky = length(dNormdy) / lenPosy;

                    // Curvature smoothing - gürültüyü azalt
                    kx = max(kx, 0.0);
                    ky = max(ky, 0.0);

                    // Maksimum principal curvature
                    float curvature = max(kx, ky);

                    // Yumuşatma - küçük değişimleri filtrele
                    curvature = pow(curvature, 0.95);

                    // Radius hesaplama (r = 1/κ)
                    // Profesyonel CAD sistemlerinde: radius = 1 / curvature
                    // curvatureScale: Ekran uzayı → dünya uzayı kalibrasyonu
                    float estimatedRadius;

                    if (curvature < 0.00001) {
                        // Düz yüzey veya çok düşük curvature
                        estimatedRadius = 999.0;
                    } else {
                        // r = 1/κ * kalibrasyon faktörü
                        estimatedRadius = (1.0 / curvature) * curvatureScale;
                        estimatedRadius = clamp(estimatedRadius, 0.1, 999.0);
                    }

                    // Base color seçimi - çok yumuşak geçişli renklendirme
                    vec3 baseColor = vec3(0.8, 0.8, 0.8); // Default gri

                    // Aralık bulma ve çift taraflı yumuşak interpolasyon
                    bool colorFound = false;
                    for(int i = 0; i < 10; i++) {
                        if(i >= aralikSayisi) break;

                        float minRad = radiusRanges[i * 2];
                        float maxRad = radiusRanges[i * 2 + 1];

                        if(estimatedRadius >= minRad && estimatedRadius <= maxRad) {
                            vec3 currentColor = vec3(
                                renkler[i * 3],
                                renkler[i * 3 + 1],
                                renkler[i * 3 + 2]
                            );

                            // Geniş yumuşak geçiş bölgesi (%30)
                            float rangeSize = maxRad - minRad;
                            float blendZone = min(rangeSize * 0.30, 2.0);

                            // Alt sınıra yakın - önceki renkle blend
                            if(estimatedRadius - minRad < blendZone && i > 0) {
                                float t = (estimatedRadius - minRad) / blendZone;
                                // Çok yumuşak geçiş için smoothstep'i 2 kez uygula
                                t = smoothstep(0.0, 1.0, t);
                                t = smoothstep(0.0, 1.0, t);

                                vec3 prevColor = vec3(
                                    renkler[(i-1) * 3],
                                    renkler[(i-1) * 3 + 1],
                                    renkler[(i-1) * 3 + 2]
                                );
                                baseColor = mix(prevColor, currentColor, t);
                            }
                            // Üst sınıra yakın - sonraki renkle blend
                            else if(maxRad - estimatedRadius < blendZone && i < aralikSayisi - 1) {
                                float t = (maxRad - estimatedRadius) / blendZone;
                                // Çok yumuşak geçiş
                                t = smoothstep(0.0, 1.0, t);
                                t = smoothstep(0.0, 1.0, t);

                                vec3 nextColor = vec3(
                                    renkler[(i+1) * 3],
                                    renkler[(i+1) * 3 + 1],
                                    renkler[(i+1) * 3 + 2]
                                );
                                baseColor = mix(nextColor, currentColor, t);
                            }
                            else {
                                baseColor = currentColor;
                            }

                            colorFound = true;
                            break;
                        }
                    }

                    // Aralık dışındaysa - en yakın renge yumuşak geçiş
                    if(!colorFound && aralikSayisi > 0) {
                        // En düşük aralığın altında
                        if(estimatedRadius < radiusRanges[0]) {
                            float blend = clamp((radiusRanges[0] - estimatedRadius) / 2.0, 0.0, 1.0);
                            blend = smoothstep(0.0, 1.0, blend);
                            vec3 firstColor = vec3(renkler[0], renkler[1], renkler[2]);
                            baseColor = mix(firstColor, baseColor, blend);
                        }
                        // En yüksek aralığın üstünde
                        else if(estimatedRadius > radiusRanges[(aralikSayisi-1) * 2 + 1]) {
                            float blend = clamp((estimatedRadius - radiusRanges[(aralikSayisi-1) * 2 + 1]) / 2.0, 0.0, 1.0);
                            blend = smoothstep(0.0, 1.0, blend);
                            int lastIdx = (aralikSayisi-1) * 3;
                            vec3 lastColor = vec3(renkler[lastIdx], renkler[lastIdx+1], renkler[lastIdx+2]);
                            baseColor = mix(lastColor, baseColor, blend);
                        }
                    }

                    // === LIGHTING SYSTEM (Undercut tarzı) ===
                    vec3 viewDir = normalize(-vViewPosition);

                    // Key light
                    vec3 keyLight = normalize(keyLightPos);
                    float keyDiffuse = max(dot(viewNormal, keyLight), 0.0);

                    // Fill light
                    vec3 fillLight = normalize(fillLightPos);
                    float fillDiffuse = max(dot(viewNormal, fillLight), 0.0) * fillLightIntensity;

                    // Rim light
                    vec3 rimLight = normalize(rimLightPos);
                    float rimDiffuse = max(dot(viewNormal, rimLight), 0.0) * rimLightIntensity;

                    // === AMBIENT OCCLUSION ===
                    vec3 dx = dFdx(vNormal);
                    vec3 dy = dFdy(vNormal);
                    float normalCurvature = length(dx) + length(dy);
                    float ao = 1.0 - clamp(normalCurvature * aoIntensity, 0.0, aoMax);

                    // Toplam ışık
                    float totalLight = ambientLight + (keyDiffuse * keyLightIntensity) + fillDiffuse + rimDiffuse;
                    totalLight *= ao;
                    totalLight = clamp(totalLight, ambientLight, 1.2);

                    // === EDGE DETECTION ===
                    float edge = clamp(normalCurvature * edgeIntensity, 0.0, 1.0);

                    // Final color = base color * lighting
                    vec3 finalColor = baseColor * totalLight;

                    // Edge darkening
                    finalColor = mix(finalColor, finalColor * (1.0 - edgeDarkness), edge * edgeDarkness);

                    gl_FragColor = vec4(finalColor, 1.0);
                }
            `
        });
    }

    public analiziBaslat() {
        const seciliMeshler = this.seciliMeshleriAl();
        if (!this.shaderMaterial || seciliMeshler.length === 0) {
            return;
        }

        this.analiziDurdur();

        seciliMeshler.forEach((mesh: THREE.Mesh) => {
            if (mesh instanceof THREE.Mesh && mesh.material) {
                this.orijinalMaterials.set(mesh, mesh.material as THREE.Material | THREE.Material[]);

                const geometry = mesh.geometry as THREE.BufferGeometry;
                if (!geometry.hasAttribute('color')) {
                    const positionAttr = geometry.getAttribute('position');
                    if (positionAttr) {
                        const vertexCount = positionAttr.count;
                        const colors = new Float32Array(vertexCount * 3);
                        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
                    }
                }

                mesh.material = this.shaderMaterial!;

                if (mesh.parent) {
                    mesh.parent.userData.radiusAnalizMeshParent = true;
                }

                this.aktifMeshler.push(mesh);
            }
        });

        this.analizAktif = true;

        if (this.anaGosterim) {
            this.anaGosterim.ciz();
        }

        this.CD.detectChanges();
    }

    public analiziDurdur() {
        this.aktifMeshler.forEach(mesh => {
            const orijinalMaterial = this.orijinalMaterials.get(mesh);
            if (orijinalMaterial) {
                mesh.material = orijinalMaterial;
            }

            if (mesh.parent && mesh.parent.userData?.radiusAnalizMeshParent) {
                delete mesh.parent.userData.radiusAnalizMeshParent;
            }
        });

        this.aktifMeshler = [];
        this.orijinalMaterials.clear();
        this.analizAktif = false;

        if (this.anaGosterim) {
            this.anaGosterim.ciz();
        }

        this.CD.detectChanges();
    }

    private seciliMeshleriAl(): THREE.Mesh[] {
        const meshler: THREE.Mesh[] = [];

        if (!this.anaGosterim) {
            return meshler;
        }

        const seciliGruplar = [];

        if (this.anaGosterim.secililer?.grupSecimlerUzerinde) {
            seciliGruplar.push(this.anaGosterim.secililer.grupSecimlerUzerinde);
        }
        if (this.anaGosterim.secililer?.grupSecimler2Uzerinde) {
            seciliGruplar.push(this.anaGosterim.secililer.grupSecimler2Uzerinde);
        }

        seciliGruplar.forEach((grup: any) => {
            if (grup && grup.children) {
                grup.traverse((child: any) => {
                    if (child instanceof THREE.Mesh && child.material) {
                        meshler.push(child);
                    }
                });
            }
        });

        if (meshler.length === 0 && this.anaGosterim.sahneler?.[0]) {
            this.anaGosterim.sahneler[0].traverse((child: any) => {
                if (child instanceof THREE.Mesh &&
                    child.visible &&
                    child.name !== 'Ölçümler' &&
                    child.userData.type !== 'FikoOlcumler') {
                    meshler.push(child);
                }
            });
        }

        return meshler;
    }

    // Mold Yönü Güncelleme (undercut tarzı)
    public moldYonunuGuncelle(eksen: 'x' | 'y' | 'z') {
        this.secilenFaceNormal = null;

        switch (eksen) {
            case 'x':
                this.moldDirection.set(1, 0, 0);
                break;
            case 'y':
                this.moldDirection.set(0, 1, 0);
                break;
            case 'z':
                this.moldDirection.set(0, 0, 1);
                break;
        }

        if (this.shaderMaterial) {
            // Use copy to update uniform value instead of creating new vector
            this.shaderMaterial.uniforms.moldDirection.value.copy(this.moldDirection);
        }

        if (this.analizAktif && this.anaGosterim) {
            this.anaGosterim.ciz();
        }

        this.CD.detectChanges();
    }

    public yonSec() {
        this.yuzeySecimiBaslat();
    }

    public resetYuzeySecimi() {
        this.secilenFaceNormal = null;
        this.moldDirection.set(0, 0, 1);

        if (this.shaderMaterial) {
            // Use copy instead of clone
            this.shaderMaterial.uniforms.moldDirection.value.copy(this.moldDirection);
        }

        this.yuzeySecimiBaslat();
        this.CD.detectChanges();
    }

    private yuzeySecimiBaslat() {
        if (!this.anaGosterim || this.fikoSecimYuzey?.aktif) {
            return;
        }

        const seciliMeshler = this.seciliMeshleriAl();
        if (seciliMeshler.length === 0) {
            return;
        }

        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }

        this.fikoSecimYuzey = new FikoSecililer(this.anaGosterim);
        this.fikoSecimYuzey.rayObje = seciliMeshler;
        this.fikoSecimYuzey.filitreler = ["Mesh"];
        this.fikoSecimYuzey.raycaster.params.Mesh = { threshold: 0 };

        const sub = this.fikoSecimYuzey.tiklandi.subscribe((kesisim: any) => {
            let faceNormal: THREE.Vector3 | null = null;
            const actualMesh = kesisim?.altObje || kesisim?.object;
            const mesh = actualMesh as THREE.Mesh;

            if (kesisim?.face?.normal) {
                // Use pre-allocated buffers for matrix operations
                this._tmpMat3.getNormalMatrix(mesh.matrixWorld);
                // Reuse _tmpVec3A for the transformed normal
                faceNormal = this._tmpVec3A.copy(kesisim.face.normal).applyMatrix3(this._tmpMat3).normalize();
            } else if (mesh.geometry && kesisim.faceIndex !== undefined) {
                const geometry = mesh.geometry as THREE.BufferGeometry;
                const normalAttr = geometry.getAttribute('normal') as THREE.BufferAttribute;
                if (normalAttr) {
                    const faceIndex = kesisim.faceIndex;
                    // Use pre-allocated buffer for local normal
                    this._tmpVec3B.set(
                        normalAttr.getX(faceIndex),
                        normalAttr.getY(faceIndex),
                        normalAttr.getZ(faceIndex)
                    );
                    this._tmpMat3.getNormalMatrix(mesh.matrixWorld);
                    faceNormal = this._tmpVec3A.copy(this._tmpVec3B).applyMatrix3(this._tmpMat3).normalize();
                }
            }

            if (faceNormal) {
                // Clone only once when storing result
                this.secilenFaceNormal = faceNormal.clone();
                this.moldDirection.copy(faceNormal);

                if (this.shaderMaterial) {
                    this.shaderMaterial.uniforms.moldDirection.value.copy(this.moldDirection);
                }

                if (this.analizAktif && this.anaGosterim) {
                    this.anaGosterim.ciz();
                }

                this.yuzeySecimiDurdur();
                this.CD.detectChanges();
            }
        });

        this.subsList.push(sub);

        this.fikoSecimYuzey.baslat();
        this.fikoSecimYuzey.aktif = true;

        this.yonSecimAktif = true;
        this.CD.detectChanges();
    }

    private yuzeySecimiDurdur() {
        if (this.fikoSecimYuzey) {
            this.fikoSecimYuzey.aktif = false;
            this.fikoSecimYuzey.bitir();
            this.fikoSecimYuzey = null;
        }
        this.yonSecimAktif = false;
        this.CD.detectChanges();
    }

    // UI Event Handlers
    onToolTypeChange(event: Event) {
        const target = event.target as HTMLSelectElement;
        this.settings.toolConfig.toolType = target.value as any;
        this.updateMaterial();
    }

    toolTypeSec(type: 'ball' | 'flat' | 'bullnose' | 'chamfer') {
        this.settings.toolConfig.toolType = type;
        this.updateMaterial();
        this.CD.detectChanges();
    }

    onToolRadiusChange(event: Event) {
        const target = event.target as HTMLInputElement;
        this.settings.toolConfig.radius = parseFloat(target.value);
        this.updateMaterial();
    }

    onCurvatureScaleChange(event: Event) {
        const target = event.target as HTMLInputElement;
        this.curvatureScale = parseFloat(target.value);

        if (this.shaderMaterial) {
            this.shaderMaterial.uniforms.curvatureScale.value = this.curvatureScale;
            if (this.analizAktif && this.anaGosterim) {
                this.anaGosterim.ciz();
            }
        }

        this.CD.detectChanges();
    }

    private updateMaterial() {
        if (!this.shaderMaterial) return;

        // Shader'ı yeniden oluştur
        this.shaderOlustur();

        // Aktif meshlere yeni material uygula
        if (this.analizAktif) {
            this.aktifMeshler.forEach(mesh => {
                mesh.material = this.shaderMaterial!;
            });
            this.anaGosterim?.ciz();
        }

        this.CD.detectChanges();
    }

    resetSettings() {
        this.settings = {
            toolConfig: {
                toolType: 'ball',
                radius: 5.0
            },
            minRadius: 0.1,
            maxRadius: 50.0,
            showHeatmap: true
        };
        this.updateMaterial();
    }

    // Radius aralık yönetimi
    aralikDegisti() {
        this.shaderOlustur();
        if (this.analizAktif) {
            this.aktifMeshler.forEach(mesh => {
                mesh.material = this.shaderMaterial!;
            });
            this.anaGosterim?.ciz();
        }
        this.CD.detectChanges();
    }

    aralikEkle() {
        if (this.radiusRanges.length >= 10) {
            return;
        }

        this.radiusRanges.push({
            min: this.yeniAralik.min,
            max: this.yeniAralik.max,
            renk: this.yeniAralik.renk,
            isim: this.yeniAralik.isim
        });

        // Formu sıfırla
        this.yeniAralik = { min: 0, max: 10, renk: '#ffffff', isim: 'Yeni Aralık' };

        this.aralikDegisti();
    }

    aralikSil(index: number) {
        this.radiusRanges.splice(index, 1);
        this.aralikDegisti();
    }

    ngOnDestroy() {
        this.bitir();

        this.subsList.forEach(sub => {
            if (sub && sub.unsubscribe) {
                sub.unsubscribe();
            }
        });

        this.haritaOlaylar.forEach((value: any, key: string) => {
            if (value && typeof value === 'function') {
                value();
            }
        });

        if (this.shaderMaterial) {
            this.shaderMaterial.dispose();
        }
    }
}
